import { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Pencil, Trash2, Power, Store, Globe, Eye, EyeOff, CheckCircle2, TrendingUp } from "lucide-react";
import { api, inr } from "../../lib/api";
import { Field, Modal } from "./AdminRestaurants";
import { toast } from "sonner";
import AdminShopPicker from "../../components/AdminShopPicker";

const EMPTY = {
    code: "",
    title: "",
    description: "",
    discount_type: "PERCENT",
    discount_value: 10,
    min_cart_value: 199,
    max_discount: 100,
    status: "ACTIVE",
    is_visible: true,
    zone_ids: [],
    scope: "TENANT",
    restaurant_id: "",
    discount_charged_to: "TENANT",
    in_store_eligible: true,
    unlock_coin_cost: 0,
    unlock_validity_hours: "",
};

const TABS = [
    { key: "ALL", label: "All" },
    { key: "TENANT", label: "Tenant" },
    { key: "SHOP", label: "Shop" },
    { key: "PENDING", label: "Pending approval" },
    // iter101 — usage / redemption report tab
    { key: "REPORT", label: "Redemption report" },
];

export default function AdminCoupons() {
    const [rows, setRows] = useState([]);
    const [zones, setZones] = useState([]);
    const [shops, setShops] = useState([]);
    const [tab, setTab] = useState("ALL");
    // iter84 — optional shop filter (searchable picker). "" = no filter.
    const [shopFilter, setShopFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(EMPTY);
    // iter107 — inline redemption stats on each coupon card. Fetched
    // once alongside the coupon list from the same /redemption-report
    // aggregate that powers the report tab, keyed by coupon code.
    const [redeemStats, setRedeemStats] = useState({});

    const load = () => api.get("/admin/coupons").then(({ data }) => setRows(data));
    const loadRedemptions = () => {
        // No date filter here — we want the LIFETIME redemption count
        // per coupon on the list view. The report tab has its own
        // filters for windowed analysis.
        api.get("/admin/coupons/redemption-report")
            .then(({ data }) => {
                const map = {};
                (data.rows || []).forEach((r) => {
                    map[r.coupon_code] = {
                        redemptions: r.redemptions || 0,
                        unique_users: r.unique_users_count || 0,
                        total_discount: r.total_discount || 0,
                        last_used: r.last_used || null,
                    };
                });
                setRedeemStats(map);
            })
            .catch(() => { /* soft-fail — cards still render without the badge */ });
    };
    useEffect(() => {
        load();
        loadRedemptions();
        api.get("/admin/zones").then(({ data }) => setZones((data || []).filter(z => z.status !== "DELETED")));
        api.get("/admin/restaurants").then(({ data }) => setShops((data || []).filter(r => r.status !== "DELETED")));
    }, []);

    const shopName = useMemo(() => {
        const m = {};
        shops.forEach((r) => (m[r.id] = r.name));
        return m;
    }, [shops]);

    const filtered = useMemo(() => {
        let list = rows;
        if (tab === "PENDING") {
            list = list.filter((c) => c.scope === "SHOP" && (!c.is_visible || c.status === "PENDING"));
        } else if (tab !== "ALL") {
            list = list.filter((c) => (c.scope || "TENANT") === tab);
        }
        // iter84 — when a shop is picked, restrict to coupons attached to
        // that shop (SHOP-scoped only; TENANT coupons apply everywhere so
        // hiding them when filtering by shop would mislead the operator).
        if (shopFilter) {
            list = list.filter((c) => c.restaurant_id === shopFilter);
        }
        return list;
    }, [rows, tab, shopFilter]);

    const counts = useMemo(() => ({
        ALL: rows.length,
        TENANT: rows.filter((c) => (c.scope || "TENANT") === "TENANT").length,
        SHOP: rows.filter((c) => c.scope === "SHOP").length,
        PENDING: rows.filter((c) => c.scope === "SHOP" && (!c.is_visible || c.status === "PENDING")).length,
    }), [rows]);

    const openCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (c) => {
        setEdit(c);
        setForm({
            code: c.code, title: c.title || "", description: c.description || "",
            discount_type: c.discount_type || "PERCENT",
            discount_value: c.discount_value ?? 10,
            min_cart_value: c.min_cart_value ?? 0,
            max_discount: c.max_discount ?? 0,
            status: c.status || "ACTIVE",
            is_visible: c.is_visible !== false,
            zone_ids: Array.isArray(c.zone_ids) ? c.zone_ids : [],
            scope: c.scope || "TENANT",
            restaurant_id: c.restaurant_id || "",
            discount_charged_to: c.discount_charged_to || (c.scope === "SHOP" ? "SHOP" : "TENANT"),
            in_store_eligible: c.in_store_eligible !== false,
            unlock_coin_cost: Number(c.unlock_coin_cost) || 0,
            unlock_validity_hours: c.unlock_validity_hours ?? "",
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.code.trim()) return toast.error("Coupon code is required");
        if (form.scope === "SHOP" && !form.restaurant_id) return toast.error("Shop is required for SHOP-scoped coupons");
        setBusy(true);
        try {
            const payload = {
                ...form,
                code: form.code.toUpperCase(),
                discount_value: Number(form.discount_value) || 0,
                min_cart_value: Number(form.min_cart_value) || 0,
                max_discount: Number(form.max_discount) || null,
                zone_ids: form.zone_ids || [],
                restaurant_id: form.scope === "SHOP" ? form.restaurant_id : null,
                in_store_eligible: !!form.in_store_eligible,
                unlock_coin_cost: Math.max(0, Number(form.unlock_coin_cost) || 0),
                unlock_validity_hours: form.unlock_validity_hours === "" || form.unlock_validity_hours == null
                    ? null
                    : Math.max(1, Math.floor(Number(form.unlock_validity_hours))),
            };
            if (edit) await api.patch(`/admin/coupons/${edit.id}`, payload);
            else await api.post(`/admin/coupons`, payload);
            toast.success(edit ? "Coupon updated" : "Coupon created");
            setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const toggle = async (c) => {
        const next = c.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        await api.patch(`/admin/coupons/${c.id}`, { status: next });
        toast.success(`${c.code} → ${next}`);
        load();
    };
    const toggleVisible = async (c) => {
        await api.patch(`/admin/coupons/${c.id}`, { is_visible: !c.is_visible });
        toast.success(`${c.code} is ${c.is_visible ? "hidden" : "visible"}`);
        load();
    };
    const approve = async (c) => {
        await api.patch(`/admin/coupons/${c.id}`, { is_visible: true, status: "ACTIVE" });
        toast.success(`${c.code} approved & live`);
        load();
    };
    const flipCharge = async (c) => {
        // This flips who actually pays for the discount — for a SHOP
        // coupon, flipping to TENANT silently takes the cost off the
        // merchant's payout and puts it on the platform's, or vice
        // versa. It was a bare one-click pill sitting next to other
        // small action buttons with no confirmation, so a stray tap
        // (e.g. while approving a merchant's pending coupon) could
        // reverse who absorbs a merchant-created SHOP coupon without
        // anyone noticing — user-reported: a merchant coupon showing
        // "admin absorbs" despite being created correctly as SHOP.
        const next = (c.discount_charged_to || (c.scope === "SHOP" ? "SHOP" : "TENANT")) === "TENANT" ? "SHOP" : "TENANT";
        if (!window.confirm(`Change who pays for "${c.code}"'s discount from ${c.discount_charged_to || (c.scope === "SHOP" ? "SHOP" : "TENANT")} to ${next}?`)) return;
        await api.patch(`/admin/coupons/${c.id}`, { discount_charged_to: next });
        toast.success(`${c.code} charged to ${next}`);
        load();
    };
    const setUnlockCost = async (c, raw) => {
        const v = Math.max(0, Math.floor(Number(raw) || 0));
        if (v === Number(c.unlock_coin_cost || 0)) return;
        try {
            await api.patch(`/admin/coupons/${c.id}`, { unlock_coin_cost: v });
            toast.success(`${c.code} unlock cost = ${v} coins`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const toggleInStore = async (c) => {
        const next = c.in_store_eligible === false;
        try {
            await api.patch(`/admin/coupons/${c.id}`, { in_store_eligible: next });
            toast.success(`${c.code} ${next ? "shown" : "hidden"} on Store Offers`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const remove = async (c) => {
        if (!confirm(`Delete coupon "${c.code}"? Past redemptions will keep their reference.`)) return;
        try {
            const { data } = await api.delete(`/admin/coupons/${c.id}`);
            toast.success(data?.mode === "archived" ? `Archived — ${data.orders} past order(s) kept` : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const toggleZone = (zid) => {
        const cur = form.zone_ids || [];
        const next = cur.includes(zid) ? cur.filter((x) => x !== zid) : [...cur, zid];
        setForm({ ...form, zone_ids: next });
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Coupons</h1>
                    <p className="text-sm text-muted-foreground mt-1">{filtered.length} of {rows.length}</p>
                </div>
                <button data-testid="add-coupon" onClick={openCreate} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> New Coupon</button>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`coupon-tab-${t.key.toLowerCase()}`}
                        onClick={() => setTab(t.key)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${tab === t.key ? "bg-foreground text-background border-foreground" : "bg-card border-border text-muted-foreground"}`}
                    >
                        {t.label}
                        <span className="ml-1.5 opacity-80">{counts[t.key]}</span>
                    </button>
                ))}
                {/* iter84 — searchable shop filter. "All shops" clears
                    the restriction; picking one narrows to SHOP-scoped
                    coupons attached to that shop. */}
                <div className="ml-auto">
                    <AdminShopPicker
                        testIdPrefix="coupons-shop-filter"
                        value={shopFilter}
                        onChange={setShopFilter}
                        allShopsOption
                        shopsOverride={shops}
                    />
                </div>
            </div>

            {tab === "REPORT" ? (
                <RedemptionReport zones={zones} shops={shops} />
            ) : (
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map((c) => {
                    const active = c.status === "ACTIVE";
                    const isShop = c.scope === "SHOP";
                    const pending = isShop && (!c.is_visible || c.status === "PENDING");
                    // iter107 — inline redemption stats. When there are
                    // no redemptions yet we still show a subtle "Never
                    // used" pill so admins can spot dead-weight coupons
                    // at a glance.
                    const stats = redeemStats[c.code];
                    return (
                        <div key={c.id} data-testid={`coupon-admin-${c.code}`} className={`bg-card border ${pending ? "border-amber-300" : "border-dashed"} rounded-2xl p-4 ${active ? "border-primary/30" : "opacity-90"}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="font-display font-bold text-xl">{c.code}</p>
                                    <p className="font-semibold text-sm mt-1 line-clamp-1">{c.title}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full shrink-0 ${active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{c.status}</span>
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${isShop ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}>
                                        {isShop ? <Store size={9} /> : <Globe size={9} />} {c.scope || "TENANT"}
                                    </span>
                                </div>
                            </div>
                            {/* iter107 — Redemption stats. Clickable
                                → deep-link into the Redemption report
                                tab with this coupon pre-filled. */}
                            {stats && stats.redemptions > 0 ? (
                                <button
                                    type="button"
                                    data-testid={`coupon-redemptions-${c.code}`}
                                    onClick={() => setTab("REPORT")}
                                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100 transition"
                                    title="View redemption details in the Redemption report tab"
                                >
                                    <TrendingUp size={11} />
                                    <span>{stats.redemptions}</span>
                                    <span className="opacity-70">redemption{stats.redemptions === 1 ? "" : "s"}</span>
                                    <span className="opacity-40">·</span>
                                    <span>{stats.unique_users}</span>
                                    <span className="opacity-70">user{stats.unique_users === 1 ? "" : "s"}</span>
                                </button>
                            ) : (
                                <span
                                    data-testid={`coupon-redemptions-${c.code}`}
                                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground bg-muted/40 rounded-full px-2 py-0.5"
                                >
                                    Never used
                                </span>
                            )}
                            {isShop && c.restaurant_id && (
                                <p className="text-[11px] text-muted-foreground mt-1">Shop: <strong>{shopName[c.restaurant_id] || "—"}</strong></p>
                            )}
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.description}</p>
                            <div className="mt-2 text-xs text-muted-foreground">
                                Min: {inr(c.min_cart_value)} •{" "}
                                {c.discount_type === "PERCENT" ? `${c.discount_value}% off` : c.discount_type === "FLAT" ? `${inr(c.discount_value)} off` : "Free delivery"}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1 items-center">
                                <button
                                    data-testid={`coupon-charged-${c.code}`}
                                    onClick={() => flipCharge(c)}
                                    title="Flip who absorbs the discount"
                                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${c.discount_charged_to === "SHOP" ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}
                                >
                                    Charged: {c.discount_charged_to || (c.scope === "SHOP" ? "SHOP" : "TENANT")}
                                </button>
                                {/* iter43e: in-store eligibility + inline coin-cost edit. */}
                                <button
                                    type="button"
                                    onClick={() => toggleInStore(c)}
                                    data-testid={`coupon-instore-${c.code}`}
                                    title={c.in_store_eligible === false ? "Hidden from Store Offers" : "Shown on Store Offers"}
                                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${c.in_store_eligible === false ? "bg-muted text-muted-foreground line-through" : "bg-amber-100 text-amber-700"}`}
                                >
                                    In-store
                                </button>
                                <span
                                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${c.in_store_eligible === false ? "bg-muted text-muted-foreground" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"}`}
                                    title="Click number to edit unlock coin cost"
                                >
                                    🪙
                                    <UnlockCostInline
                                        value={Number(c.unlock_coin_cost || 0)}
                                        disabled={c.in_store_eligible === false}
                                        testid={`coupon-unlock-cost-${c.code}`}
                                        onCommit={(v) => setUnlockCost(c, v)}
                                    />
                                </span>
                                {(c.zone_ids || []).length === 0 ? (
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">All zones</span>
                                ) : (
                                    (c.zone_ids || []).map((zid) => (
                                        <span key={zid} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                                            {zones.find((z) => z.id === zid)?.name || zid}
                                        </span>
                                    ))
                                )}
                            </div>
                            <div className="mt-3 flex items-center justify-end gap-1">
                                {pending && (
                                    <button data-testid={`coupon-approve-${c.code}`} onClick={() => approve(c)} title="Approve & make live" className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50"><CheckCircle2 size={14} /></button>
                                )}
                                <button data-testid={`coupon-visible-${c.code}`} onClick={() => toggleVisible(c)} title={c.is_visible ? "Hide" : "Show"} className="p-2 rounded-lg hover:bg-muted">{c.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                                <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} title={active ? "Disable" : "Enable"} className={`p-2 rounded-lg ${active ? "text-emerald-600 hover:bg-emerald-50" : "text-muted-foreground hover:bg-muted"}`}><Power size={14} /></button>
                                <button data-testid={`coupon-edit-${c.code}`} onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                                <button data-testid={`coupon-del-${c.code}`} onClick={() => remove(c)} className="p-2 rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    );
                })}
                {filtered.length === 0 && (
                    <div className="sm:col-span-2 lg:col-span-3 text-center py-10 text-muted-foreground">No coupons in this tab.</div>
                )}
            </div>
            )}

            {open && (
                <Modal title={edit ? `Edit · ${edit.code}` : "New Coupon"} onClose={() => setOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Scope">
                            <select
                                data-testid="coupon-scope"
                                className="inp"
                                value={form.scope}
                                onChange={(e) => {
                                    // Default "Charged to" to match the new scope —
                                    // a SHOP coupon an admin sets up for a merchant
                                    // should default to the merchant paying for it
                                    // (same default the merchant's own self-service
                                    // coupon creation already uses), not silently
                                    // stay on TENANT unless someone remembers to
                                    // flip the adjacent dropdown too. Still fully
                                    // editable right next to it.
                                    setForm({ ...form, scope: e.target.value, discount_charged_to: e.target.value });
                                }}
                            >
                                <option value="TENANT">TENANT (all shops)</option>
                                <option value="SHOP">SHOP (single shop)</option>
                            </select>
                        </Field>
                        <Field label="Charged to">
                            <select data-testid="coupon-charged-to" className="inp" value={form.discount_charged_to} onChange={(e) => setForm({ ...form, discount_charged_to: e.target.value })}>
                                <option value="TENANT">TENANT absorbs</option>
                                <option value="SHOP">SHOP absorbs</option>
                            </select>
                        </Field>
                        {form.scope === "SHOP" && (
                            <Field full label="Shop">
                                <select data-testid="coupon-shop" className="inp" value={form.restaurant_id} onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })}>
                                    <option value="">Pick…</option>
                                    {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </Field>
                        )}
                        <Field label="Code"><input data-testid="coupon-code" className="inp" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SAVE50" /></Field>
                        <Field label="Type">
                            <select className="inp" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                                <option value="PERCENT">PERCENT</option>
                                <option value="FLAT">FLAT</option>
                                <option value="FREE_DELIVERY">FREE_DELIVERY</option>
                            </select>
                        </Field>
                        <Field full label="Title"><input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                        <Field full label="Description"><input className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                        <Field label="Value"><input type="number" className="inp" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} /></Field>
                        <Field label="Max Discount"><input type="number" className="inp" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} /></Field>
                        <Field full label="Min Cart Value"><input type="number" className="inp" value={form.min_cart_value} onChange={(e) => setForm({ ...form, min_cart_value: e.target.value })} /></Field>
                        {edit && (
                            <>
                                <Field label="Status">
                                    <select className="inp" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                        <option value="PENDING">PENDING</option>
                                    </select>
                                </Field>
                                <Field label="Visibility">
                                    <select className="inp" value={form.is_visible ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_visible: e.target.value === "yes" })}>
                                        <option value="yes">Visible to customers</option>
                                        <option value="no">Hidden</option>
                                    </select>
                                </Field>
                            </>
                        )}
                        <Field full label="Zone targeting">
                            <p className="text-xs text-muted-foreground -mt-1 mb-1">
                                Leave empty to make the coupon available everywhere. Pick one or more zones to restrict it to customers in those zones.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" data-testid="coupon-zone-all" onClick={() => setForm({ ...form, zone_ids: [] })}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${(!form.zone_ids || form.zone_ids.length === 0) ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                                    All zones
                                </button>
                                {zones.map((z) => {
                                    const on = (form.zone_ids || []).includes(z.id);
                                    return (
                                        <button key={z.id} type="button" data-testid={`coupon-zone-${z.id}`} onClick={() => toggleZone(z.id)}
                                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1 ${on ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
                                            {on && <span aria-hidden>✓</span>}{z.name}
                                        </button>
                                    );
                                })}
                                {zones.length === 0 && <p className="text-xs text-muted-foreground">No zones configured yet.</p>}
                            </div>
                        </Field>

                        {/* In-store unlock controls (iter43c) — tenants choose
                            whether the coupon shows on Store Offers and how
                            many coins (if any) it costs to unlock for offline
                            redemption. Default: eligible + free unlock. */}
                        <Field full label="In-store / offline redemption">
                            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                                <label className="inline-flex items-center gap-2 text-sm font-semibold">
                                    <input
                                        type="checkbox"
                                        data-testid="coupon-in-store-eligible"
                                        checked={!!form.in_store_eligible}
                                        onChange={(e) => setForm({ ...form, in_store_eligible: e.target.checked })}
                                        className="w-4 h-4"
                                    />
                                    Show on Store Offers
                                </label>
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                        Coin cost to unlock
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        data-testid="coupon-unlock-coin-cost"
                                        className="inp mt-1"
                                        value={form.unlock_coin_cost}
                                        disabled={!form.in_store_eligible}
                                        onChange={(e) => setForm({ ...form, unlock_coin_cost: e.target.value })}
                                        placeholder="0 = free"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                        Hours to redeem after unlock
                                    </label>
                                    <input
                                        type="number"
                                        min={1}
                                        step={1}
                                        data-testid="coupon-unlock-validity-hours"
                                        className="inp mt-1"
                                        value={form.unlock_validity_hours}
                                        disabled={!form.in_store_eligible}
                                        onChange={(e) => setForm({ ...form, unlock_validity_hours: e.target.value })}
                                        placeholder="∞ (full coupon expiry)"
                                    />
                                </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1.5">
                                Customers see this coupon on the Store Offers page and unlock a one-time code to show offline.
                                Set a positive coin cost to gate premium coupons behind a coin balance. 0 = free unlock.
                            </p>
                        </Field>
                    </div>
                    <button data-testid="save-coupon" disabled={busy || !form.code} onClick={save} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={16} className="animate-spin" />} {edit ? "Save changes" : "Save"}
                    </button>
                </Modal>
            )}
        </div>
    );
}



/**
 * Tiny inline-edit for ``unlock_coin_cost`` on the coupon card. Renders
 * as plain text until clicked; then becomes a 4-char number input that
 * commits on blur or Enter. Escape reverts.
 */
function UnlockCostInline({ value, disabled, onCommit, testid }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value));
    useEffect(() => { setDraft(String(value)); }, [value]);

    const commit = () => {
        setEditing(false);
        if (String(draft) === String(value)) return;
        onCommit?.(draft);
    };

    if (!editing) {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
                data-testid={testid}
                className={`font-mono tracking-wider ${disabled ? "opacity-50" : "underline-offset-2 hover:underline"}`}
            >{value}</button>
        );
    }
    return (
        <input
            type="number"
            min={0}
            step={1}
            autoFocus
            data-testid={`${testid}-input`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
            }}
            className="w-12 bg-white border border-amber-300 rounded px-1 text-[10px] font-mono outline-none"
        />
    );
}


// -----------------------------------------------------------------
// iter101 — Coupon Redemption Report.
//
// Fetches from /admin/coupons/redemption-report with optional filters
// (date range, zone, shop, coupon code). Click a row to drill in and
// see per-order detail; CSV export honours the same filters.
// -----------------------------------------------------------------
function RedemptionReport({ zones, shops }) {
    const [filters, setFilters] = useState({
        start: "", end: "", zone_id: "", restaurant_id: "", coupon_code: "",
    });
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [drill, setDrill] = useState(null); // { coupon_code, details[] }

    const load = async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
            const { data } = await api.get(`/admin/coupons/redemption-report?${params.toString()}`);
            setRows(data.rows || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load report");
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const openDetails = async (code) => {
        setBusy(true);
        try {
            const params = new URLSearchParams({ coupon_code: code });
            Object.entries(filters).forEach(([k, v]) => { if (v && k !== "coupon_code") params.set(k, v); });
            const { data } = await api.get(`/admin/coupons/redemption-report/details?${params.toString()}`);
            setDrill({ code, details: data.rows || [] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load details");
        } finally { setBusy(false); }
    };

    const downloadCsv = async (opts = {}) => {
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
            if (opts.coupon_code) params.set("coupon_code", opts.coupon_code);
            const { data, headers } = await api.get(
                `/admin/coupons/redemption-report.csv?${params.toString()}`,
                { responseType: "blob" },
            );
            const blob = new Blob([data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cd = headers["content-disposition"] || "";
            const m = /filename="?([^";]+)"?/i.exec(cd);
            a.download = m ? m[1] : "coupon-redemption-report.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "CSV export failed");
        }
    };

    const totals = rows.reduce(
        (acc, r) => ({
            redemptions: acc.redemptions + (r.redemptions || 0),
            unique_users: acc.unique_users + (r.unique_users_count || 0),
            total_discount: acc.total_discount + (r.total_discount || 0),
            gross_sales: acc.gross_sales + (r.gross_sales || 0),
        }),
        { redemptions: 0, unique_users: 0, total_discount: 0, gross_sales: 0 },
    );

    return (
        <div className="mt-4" data-testid="coupons-redemption-report">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">From</span>
                    <input data-testid="rr-start" type="date" className="rrinp mt-1"
                        value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">To</span>
                    <input data-testid="rr-end" type="date" className="rrinp mt-1"
                        value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} />
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Zone</span>
                    <select data-testid="rr-zone" className="rrinp mt-1"
                        value={filters.zone_id} onChange={(e) => setFilters({ ...filters, zone_id: e.target.value })}>
                        <option value="">All zones</option>
                        {(zones || []).map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Shop</span>
                    <select data-testid="rr-shop" className="rrinp mt-1"
                        value={filters.restaurant_id} onChange={(e) => setFilters({ ...filters, restaurant_id: e.target.value })}>
                        <option value="">All shops</option>
                        {(shops || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Coupon</span>
                    <input data-testid="rr-coupon" type="text" placeholder="e.g. SAVE20" className="rrinp mt-1 uppercase"
                        value={filters.coupon_code} onChange={(e) => setFilters({ ...filters, coupon_code: e.target.value.toUpperCase() })} />
                </label>
            </div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button data-testid="rr-apply" onClick={load} disabled={busy}
                    className="bg-foreground text-background rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Apply filters
                </button>
                <button data-testid="rr-export" onClick={() => downloadCsv()} disabled={busy || rows.length === 0}
                    className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                    Export CSV
                </button>
                <div className="ml-auto text-xs text-muted-foreground">
                    <strong>{totals.redemptions}</strong> redemptions ·
                    <strong className="ml-1">{totals.unique_users}</strong> users ·
                    <strong className="ml-1">{inr(totals.total_discount)}</strong> given
                </div>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead>
                        <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                            <th className="px-4 py-3">Coupon</th>
                            <th className="px-4 py-3 text-right">Redemptions</th>
                            <th className="px-4 py-3 text-right">Unique users</th>
                            <th className="px-4 py-3 text-right">Total discount</th>
                            <th className="px-4 py-3 text-right">Gross sales</th>
                            <th className="px-4 py-3">Last used</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.coupon_code} data-testid={`rr-row-${r.coupon_code}`} className="border-t border-border">
                                <td className="px-4 py-3">
                                    <p className="font-display font-bold text-base">{r.coupon_code}</p>
                                    {r.coupon_meta?.title && <p className="text-xs text-muted-foreground line-clamp-1">{r.coupon_meta.title}</p>}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums font-semibold">{r.redemptions}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{r.unique_users_count}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-red-700 font-semibold">−{inr(r.total_discount)}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(r.gross_sales)}</td>
                                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{r.last_used ? new Date(r.last_used).toLocaleDateString() : "—"}</td>
                                <td className="px-4 py-3">
                                    <button data-testid={`rr-drill-${r.coupon_code}`} onClick={() => openDetails(r.coupon_code)}
                                        className="text-xs font-bold text-primary">Detail</button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No redemptions in the selected window.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {drill && (
                <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={() => setDrill(null)} data-testid="rr-drill-drawer">
                    <div className="w-full max-w-2xl bg-card h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between">
                            <div>
                                <h2 className="font-bold text-lg">{drill.code} · redemptions</h2>
                                <p className="text-xs text-muted-foreground">{drill.details.length} order{drill.details.length === 1 ? "" : "s"}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button data-testid="rr-drill-csv" onClick={() => downloadCsv({ coupon_code: drill.code })}
                                    className="text-xs font-bold bg-primary text-primary-foreground rounded-lg px-3 py-1.5">Export CSV</button>
                                <button onClick={() => setDrill(null)} className="text-xl text-muted-foreground px-2">&times;</button>
                            </div>
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                                    <th className="px-4 py-3">Order</th>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Shop</th>
                                    <th className="px-4 py-3 text-right">Discount</th>
                                    <th className="px-4 py-3">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {drill.details.map((o) => (
                                    <tr key={o.id} data-testid={`rr-detail-${o.id}`} className="border-t border-border">
                                        <td className="px-4 py-3 font-mono text-xs">{o.order_unique_id || o.id?.slice(0, 8)}</td>
                                        <td className="px-4 py-3">{o.customer_name || o.customer_mobile || "—"}</td>
                                        <td className="px-4 py-3">{o.restaurant_name || "—"}</td>
                                        <td className="px-4 py-3 text-right text-red-700 tabular-nums">−{inr(o.totals?.coupon_discount || 0)}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{o.placed_at ? new Date(o.placed_at).toLocaleString() : "—"}</td>
                                    </tr>
                                ))}
                                {drill.details.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No orders for this coupon.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <style>{`.rrinp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.55rem 0.65rem;outline:none;font-size:0.85rem;width:100%}`}</style>
        </div>
    );
}
